import { injectable, inject } from 'src/features/ioc';
import { useManagedOAuth } from 'src/lib/featureFlags/flags/useManagedOAuth';
import type { OAuth2Service } from 'src/services/OAuth2Service';
import type { IsomorphicBindings } from 'src/types/bindings';

const SESSION_RESTORED_KEY = 'oauth_session_restored';

export interface SessionState {
    isExpired: boolean;
    isRestoring: boolean;
    lastRefreshAttempt?: Date;
}

export type SessionStateCallback = (state: SessionState) => void;

/**
 * Service that monitors session state by subscribing to OAuth2Service auth events.
 *
 * This service centralizes session state management that was previously spread across
 * React hooks. It handles:
 * - OAuth2Service event subscription (TOKEN_REFRESHED, SESSION_EXPIRED, SESSION_RESTORED, LOGOUT)
 * - Session state tracking
 * - Cross-tab localStorage coordination for session restoration
 */
@injectable()
export class SessionMonitorService {
    #log: IsomorphicBindings['Logger'];
    #state: SessionState = {
        isExpired: false,
        isRestoring: false,
    };
    #subscribers: Set<SessionStateCallback> = new Set();
    #unsubscribeFromOAuth: (() => void) | null = null;
    #storageHandler: ((e: StorageEvent) => void) | null = null;
    #storageCleanupTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(
        @inject('OAuth2Service')
        private readonly oauth2Service: OAuth2Service,
        @inject('Logger') logger: IsomorphicBindings['Logger'],
        @inject('Store') private readonly store: IsomorphicBindings['Store'],
    ) {
        this.#log = logger.getSubLogger({ name: 'SessionMonitorService' }, { component: 'auth' });
        this.#initialize();
    }

    /**
     * Returns the current session state.
     */
    getState(): SessionState {
        return { ...this.#state };
    }

    /**
     * Subscribe to session state changes.
     * @returns Unsubscribe function
     */
    subscribe(callback: SessionStateCallback): () => void {
        this.#subscribers.add(callback);

        // Immediately notify with current state
        try {
            callback(this.getState());
        } catch (error) {
            this.#log.error('Error in SessionMonitorService subscriber callback during initial notification', {
                error,
            });
        }

        return () => {
            this.#subscribers.delete(callback);
        };
    }

    /**
     * Signal session restoration across tabs via localStorage.
     * Call this when a login completes in any tab.
     */
    signalSessionRestoration(): void {
        if (typeof window !== 'undefined') {
            localStorage.setItem(SESSION_RESTORED_KEY, Date.now().toString());
        }
    }

    /**
     * Clean up resources. Call when the service is no longer needed.
     */
    dispose(): void {
        this.#unsubscribeFromOAuth?.();
        this.#unsubscribeFromOAuth = null;

        if (this.#storageCleanupTimeout !== null) {
            clearTimeout(this.#storageCleanupTimeout);
            this.#storageCleanupTimeout = null;
        }

        if (this.#storageHandler && typeof window !== 'undefined') {
            window.removeEventListener('storage', this.#storageHandler);
            this.#storageHandler = null;
        }

        this.#subscribers.clear();
    }

    #initialize(): void {
        const useManagedOAuthFeatures =
            this.store.getState().featureFlags?.flags?.[useManagedOAuth.key] ?? useManagedOAuth.defaultValue;
        if (!useManagedOAuthFeatures) {
            this.#log.info('Managed OAuth features disabled, skipping event subscription');
            return;
        }

        // Subscribe to OAuth service events
        this.#unsubscribeFromOAuth = this.oauth2Service.subscribeToAuthEvents((event) => {
            if (TRACE) {
                this.#log.trace('Received auth event', { eventType: event.type });
            }

            switch (event.type) {
                case 'TOKEN_REFRESHED':
                    this.#updateState({
                        lastRefreshAttempt: new Date(),
                        isExpired: false,
                        isRestoring: false,
                    });
                    break;
                case 'SESSION_EXPIRED':
                    this.#updateState({
                        isExpired: true,
                        isRestoring: false,
                    });
                    break;
                case 'SESSION_RESTORED':
                    this.#updateState({
                        isExpired: false,
                        isRestoring: false,
                    });
                    break;
                case 'LOGOUT':
                    this.#updateState({
                        isExpired: false,
                        isRestoring: false,
                    });
                    break;
            }
        });

        // Set up cross-tab session restoration monitoring
        this.#setupStorageListener();

        this.#log.info('Initialized with managed OAuth2Service');
    }

    #setupStorageListener(): void {
        if (typeof window === 'undefined') {
            return;
        }

        this.#storageHandler = (e: StorageEvent) => {
            if (e.key === SESSION_RESTORED_KEY && e.newValue) {
                this.#log.info('Cross-tab session restoration signal detected');

                // Another tab completed login, indicate we're restoring
                if (this.#state.isExpired) {
                    this.#updateState({
                        ...this.#state,
                        isRestoring: true,
                    });
                }

                // Clear the storage event flag after a short delay
                if (this.#storageCleanupTimeout !== null) {
                    clearTimeout(this.#storageCleanupTimeout);
                }
                this.#storageCleanupTimeout = setTimeout(() => {
                    this.#storageCleanupTimeout = null;
                    localStorage.removeItem(SESSION_RESTORED_KEY);
                }, 100);
            }
        };

        window.addEventListener('storage', this.#storageHandler);
    }

    #updateState(newState: SessionState): void {
        this.#state = newState;

        // Notify all subscribers
        const state = this.getState();
        for (const callback of this.#subscribers) {
            try {
                callback(state);
            } catch (error) {
                this.#log.error('Error in SessionMonitorService subscriber callback', { error });
            }
        }
    }
}
